'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface SwitchPort {
  id: string;
  device_id: string;
  numero_porta: number;
  nome: string;
  stato: 'attiva' | 'inattiva' | 'errore' | 'disabilitata' | 'poe';
  vlan: string;
  velocita: string;
  duplex: string;
  poe_watt: number;
  mac_connesso: string;
  device_connesso: string;
  errori_rx: number;
  errori_tx: number;
  traffico_rx_mbps: number;
  traffico_tx_mbps: number;
  ultimo_aggiornamento: string;
  note: string;
}

interface Device {
  id: string;
  hostname: string;
  categoria: string;
}

const statoColors: Record<string, string> = {
  attiva: 'bg-green-500/20 text-green-400 border-green-500/30',
  inattiva: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  errore: 'bg-red-500/20 text-red-400 border-red-500/30',
  disabilitata: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  poe: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
};

const statoLabels: Record<string, string> = {
  attiva: 'Attiva',
  inattiva: 'Inattiva',
  errore: 'Errore',
  disabilitata: 'Disabilitata',
  poe: 'PoE',
};

export default function SwitchPortsContent() {
  const [ports, setPorts] = useState<SwitchPort[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('all');
  const [statoFilter, setStatoFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [editingPort, setEditingPort] = useState<SwitchPort | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newPort, setNewPort] = useState({ device_id: '', numero_porta: 1, nome: '', stato: 'inattiva' as const, vlan: '', velocita: '1Gbps', duplex: 'full', note: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [portsRes, devicesRes] = await Promise.all([
        supabase.from('switch_ports').select('*').order('numero_porta'),
        supabase.from('devices').select('id, hostname, categoria').eq('categoria', 'rete'),
      ]);
      if (portsRes.error) throw portsRes.error;
      setPorts(portsRes.data || []);
      setDevices(devicesRes.data || []);
    } catch (err: any) {
      toast.error('Errore caricamento porte: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredPorts = ports.filter(p => {
    const matchDevice = selectedDevice === 'all' || p.device_id === selectedDevice;
    const matchStato = statoFilter === 'all' || p.stato === statoFilter;
    return matchDevice && matchStato;
  });

  const handleUpdatePort = async (port: SwitchPort) => {
    try {
      const { error } = await supabase.from('switch_ports').update({
        stato: port.stato,
        vlan: port.vlan,
        note: port.note,
      }).eq('id', port.id);
      if (error) throw error;
      toast.success(`Porta ${port.nome} aggiornata`);
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore aggiornamento: ' + err.message);
    }
  };

  const handleAddPort = async () => {
    if (!newPort.device_id) { toast.error('Seleziona un dispositivo'); return; }
    if (!newPort.nome.trim()) { toast.error('Nome porta obbligatorio'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('switch_ports').insert({
        device_id: newPort.device_id,
        numero_porta: newPort.numero_porta,
        nome: newPort.nome,
        stato: newPort.stato,
        vlan: newPort.vlan || null,
        velocita: newPort.velocita,
        duplex: newPort.duplex,
        note: newPort.note,
        poe_watt: 0,
        errori_rx: 0,
        errori_tx: 0,
        traffico_rx_mbps: 0,
        traffico_tx_mbps: 0,
      });
      if (error) throw error;
      toast.success('Porta aggiunta');
      setShowAddModal(false);
      setNewPort({ device_id: '', numero_porta: 1, nome: '', stato: 'inattiva', vlan: '', velocita: '1Gbps', duplex: 'full', note: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePort = async (id: string) => {
    try {
      const { error } = await supabase.from('switch_ports').delete().eq('id', id);
      if (error) throw error;
      toast.success('Porta eliminata');
      setDeleteConfirmId(null);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const statoCounts = {
    attiva: ports.filter(p => p.stato === 'attiva').length,
    inattiva: ports.filter(p => p.stato === 'inattiva').length,
    errore: ports.filter(p => p.stato === 'errore').length,
    disabilitata: ports.filter(p => p.stato === 'disabilitata').length,
    poe: ports.filter(p => p.stato === 'poe').length,
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      {/* Header */}
      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Porte Switch</h1>
            <p className="text-sm text-secondary-color mt-0.5">Monitoraggio stato porte switch in tempo reale</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg text-sm font-semibold hover:bg-sky-500/20 transition-all"
            >
              <Icon name="ArrowPathIcon" size={15} />
              Aggiorna
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all"
            >
              <Icon name="PlusIcon" size={15} />
              Aggiungi Porta
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-5 gap-3 mt-4">
          {Object.entries(statoCounts).map(([stato, count]) => (
            <div key={stato} className={`rounded-lg border px-3 py-2 ${statoColors[stato]}`}>
              <p className="text-xs font-bold uppercase tracking-wide opacity-70">{statoLabels[stato]}</p>
              <p className="text-2xl font-bold mt-0.5">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 border-b border-border bg-surface flex items-center gap-3 flex-wrap">
        <div className="relative">
          <select
            value={selectedDevice}
            onChange={e => setSelectedDevice(e.target.value)}
            className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500/50 appearance-none pr-7 cursor-pointer"
          >
            <option value="all">Tutti i dispositivi</option>
            {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={statoFilter}
            onChange={e => setStatoFilter(e.target.value)}
            className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500/50 appearance-none pr-7 cursor-pointer"
          >
            <option value="all">Tutti gli stati</option>
            <option value="attiva">Attiva</option>
            <option value="inattiva">Inattiva</option>
            <option value="errore">Errore</option>
            <option value="disabilitata">Disabilitata</option>
            <option value="poe">PoE</option>
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <span className="text-sm text-muted-color font-semibold ml-auto">{filteredPorts.length} porte</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPorts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon name="CircleStackIcon" size={40} className="text-muted-color" />
            <p className="text-secondary-color font-semibold">Nessuna porta trovata</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface border-b border-border z-10">
              <tr>
                {['Porta', 'Stato', 'VLAN', 'Velocità', 'MAC Connesso', 'Dispositivo', 'RX Mbps', 'TX Mbps', 'PoE (W)', 'Errori', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPorts.map(port => (
                <tr key={port.id} className="hover:bg-surface-elevated/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-white font-semibold">{port.nome || `Gi0/${port.numero_porta}`}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold border ${statoColors[port.stato]}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {statoLabels[port.stato]}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-secondary-color">{port.vlan || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color">{port.velocita}</td>
                  <td className="px-4 py-3 font-mono text-xs text-secondary-color">{port.mac_connesso || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color">{port.device_connesso || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono font-semibold ${port.traffico_rx_mbps > 500 ? 'text-amber-400' : 'text-green-400'}`}>
                      {port.traffico_rx_mbps.toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono font-semibold ${port.traffico_tx_mbps > 500 ? 'text-amber-400' : 'text-sky-400'}`}>
                      {port.traffico_tx_mbps.toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-secondary-color">{port.poe_watt > 0 ? port.poe_watt.toFixed(1) : '—'}</td>
                  <td className="px-4 py-3">
                    {(port.errori_rx > 0 || port.errori_tx > 0) ? (
                      <span className="text-red-400 font-mono text-xs">{port.errori_rx + port.errori_tx}</span>
                    ) : (
                      <span className="text-muted-color">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditingPort(port); setShowModal(true); }}
                        className="p-1.5 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors"
                        title="Modifica"
                      >
                        <Icon name="PencilIcon" size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(port.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors"
                        title="Elimina"
                      >
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-2">Elimina Porta</h3>
            <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare questa porta? L'azione non può essere annullata.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={() => handleDeletePort(deleteConfirmId)} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all">Elimina</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Port Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Aggiungi Porta Switch</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1">Dispositivo *</label>
                <select value={newPort.device_id} onChange={e => setNewPort(p => ({ ...p, device_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                  <option value="">— Seleziona dispositivo —</option>
                  {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Nome Porta *</label>
                  <input value={newPort.nome} onChange={e => setNewPort(p => ({ ...p, nome: e.target.value }))} placeholder="es. Gi0/1" className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Numero Porta</label>
                  <input type="number" min="1" value={newPort.numero_porta} onChange={e => setNewPort(p => ({ ...p, numero_porta: parseInt(e.target.value) || 1 }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Stato</label>
                  <select value={newPort.stato} onChange={e => setNewPort(p => ({ ...p, stato: e.target.value as any }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                    <option value="attiva">Attiva</option>
                    <option value="inattiva">Inattiva</option>
                    <option value="disabilitata">Disabilitata</option>
                    <option value="poe">PoE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">VLAN</label>
                  <input value={newPort.vlan} onChange={e => setNewPort(p => ({ ...p, vlan: e.target.value }))} placeholder="es. 10" className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Velocità</label>
                  <select value={newPort.velocita} onChange={e => setNewPort(p => ({ ...p, velocita: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                    <option value="10Mbps">10 Mbps</option>
                    <option value="100Mbps">100 Mbps</option>
                    <option value="1Gbps">1 Gbps</option>
                    <option value="10Gbps">10 Gbps</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Duplex</label>
                  <select value={newPort.duplex} onChange={e => setNewPort(p => ({ ...p, duplex: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                    <option value="full">Full</option>
                    <option value="half">Half</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1">Note</label>
                <input value={newPort.note} onChange={e => setNewPort(p => ({ ...p, note: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border">
              <button onClick={() => setShowAddModal(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleAddPort} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Aggiungi Porta'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && editingPort && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Modifica Porta {editingPort.nome}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Stato</label>
                <select
                  value={editingPort.stato}
                  onChange={e => setEditingPort({ ...editingPort, stato: e.target.value as any })}
                  className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50"
                >
                  <option value="attiva">Attiva</option>
                  <option value="inattiva">Inattiva</option>
                  <option value="errore">Errore</option>
                  <option value="disabilitata">Disabilitata</option>
                  <option value="poe">PoE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">VLAN</label>
                <input
                  type="text"
                  value={editingPort.vlan}
                  onChange={e => setEditingPort({ ...editingPort, vlan: e.target.value })}
                  className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50"
                  placeholder="es. VLAN-10"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea
                  value={editingPort.note}
                  onChange={e => setEditingPort({ ...editingPort, note: e.target.value })}
                  rows={3}
                  className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-all">
                Annulla
              </button>
              <button onClick={() => handleUpdatePort(editingPort)} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all">
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
